import { styled } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { AuthenticationProvider, useToken } from "@packsize/authentication";
import { FeatureFlagsProvider, useFeatureFlags } from "@packsize/feature-flags";
import { LoadingScreen } from "@packsize/components";
import { ToastProvider } from "@packsize/toast";
import { getUnmanagedApplicationInsights } from "@packsize/application-monitoring";

import { AppInsightsProvider } from "@/contexts/application-insights";
import { ProductionProvider } from "@/contexts/production";
import Pendo from "@/components/pendo";
import { baseRoutes } from "@/api/base-routes";
import { queryClient } from "@/lib/query-client";
import { Locale } from "@/locale";
import { AppRoutes } from "@/routes";
import { ThemeProvider } from "@/theme-provider";
import { useSetupPageTitle } from "@/hooks/use-setup-page-title";
import { getLoginDestination } from "@/utils/get-login-destination";
import { applicationName, launchDarklyOptions, storagePrefix } from "@/environment";

const StyledMain = styled("main")`
	width: 100vw;
	height: 100vh;
`;

window.addEventListener("vite:preloadError", (event) => {
	getUnmanagedApplicationInsights().trackEvent({
		name: "PreloadError",
		properties: event,
	});
	window.location.reload();
});

const AuthenticationSetup = () => {
	const reactQueryClient = useQueryClient();
	const { bearerToken, decodedToken, tokenValid } = useToken();
	const { setFeatureFlagIdentity } = useFeatureFlags();
	const featureFlagIdentitySent = useRef<string | null>(null);

	useSetupPageTitle();

	const clearUserQueries = useCallback(() => {
		reactQueryClient.removeQueries();
	}, [reactQueryClient]);

	useEffect(() => {
		if (tokenValid === false) {
			clearUserQueries();
		}
	}, [clearUserQueries, tokenValid]);

	useEffect(() => {
		clearUserQueries();
	}, [clearUserQueries, bearerToken]);

	useEffect(() => {
		if (bearerToken === null) {
			// TODO @eb these should be closer to where they are being set and not handled here
			sessionStorage.removeItem("siteSelectShown");
			localStorage.removeItem("dimension-products-search-term");
		}
	}, [bearerToken]);

	useEffect(() => {
		const appInsights = getUnmanagedApplicationInsights();

		if (decodedToken === null) {
			appInsights.clearAuthenticatedUserContext();
			return;
		}

		appInsights.setAuthenticatedUserContext(decodedToken.id, decodedToken.tid, true);
	}, [decodedToken]);

	useEffect(() => {
		if (!decodedToken?.id) {
			if (featureFlagIdentitySent.current !== null) {
				setFeatureFlagIdentity(undefined);
				featureFlagIdentitySent.current = null;
			}
			return;
		}

		const key = `${decodedToken.id}|${decodedToken.tid}`;
		if (featureFlagIdentitySent.current === key) {
			return;
		}
		featureFlagIdentitySent.current = key;

		setFeatureFlagIdentity({
			userId: decodedToken.id,
			tenantId: decodedToken.tid,
			companyId: decodedToken.cid,
			salesforceId: decodedToken.CustomerId,
			email: decodedToken.email,
		});
	}, [decodedToken, setFeatureFlagIdentity]);

	return null;
};

const App = () => {
	return (
		<StyledMain>
			<FeatureFlagsProvider launchDarklyOptions={launchDarklyOptions} loadingComponent={<LoadingScreen />}>
				<AuthenticationProvider
					identityBaseRoute={baseRoutes.identity}
					applicationName={applicationName}
					storagePrefix={storagePrefix}
					getLoginDestination={getLoginDestination}
				>
					<AuthenticationSetup />
					<Pendo>
						<ProductionProvider>
							<ToastProvider>
								<AppRoutes />
							</ToastProvider>
						</ProductionProvider>
					</Pendo>
				</AuthenticationProvider>
			</FeatureFlagsProvider>
		</StyledMain>
	);
};

const Layout = () => {
	return (
		<ThemeProvider>
			<QueryClientProvider client={queryClient}>
				<AppInsightsProvider>
					<Locale>
						<App />
					</Locale>
				</AppInsightsProvider>
			</QueryClientProvider>
		</ThemeProvider>
	);
};

export default Layout;
